import { ContextProvider } from '@lit/context'
import clsx from 'clsx'
import { html, nothing, PropertyValueMap, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'

import { CommonFormElementSize } from '@/common/types'
import { GenericFormElement } from '@/internal/mixin/genericFormElement'

import { CodePuncherContext, codePuncherContext } from './code-puncher.context'
import styles from './code-puncher.scss?inline'
import type DapDSCodePuncherSlot from './code-puncher-slot.component'

/**
 * `dap-ds-code-puncher`
 * @summary A composable PIN/OTP input component for entering verification codes.
 * @element dap-ds-code-puncher
 * @title - Code Puncher
 * @group code-puncher
 *
 * @property {string} value - The complete code value.
 * @property {boolean} mask - Mask input like password. Default is false.
 * @property {string} pattern - Regex pattern for allowed characters. Default is '[0-9]'.
 * @property {string} placeholder - Placeholder character for empty slots.
 * @property {'xs' | 'sm' | 'lg'} size - The size of the slots. Default is 'sm'.
 * @property {string} label - The label of the component.
 * @property {string} description - The description of the component.
 * @property {string} tooltip - The tooltip of the component.
 * @property {string} feedback - The feedback message.
 * @property {'negative' | 'positive' | 'warning'} feedbackType - The feedback type.
 * @property {'success' | 'error'} status - The status of the component.
 * @property {boolean} disabled - Disable all slots. Default is false.
 * @property {boolean} readonly - Read-only state. Default is false.
 * @property {boolean} required - Required for form validation. Default is false.
 * @property {string} name - Form element name.
 * @property {string} autocomplete - Autocomplete hint. Default is 'one-time-code'.
 *
 * @event {{ value: string, index: number }} dds-input - Fires on each digit input.
 * @event {{ value: string }} dds-change - Fires when value changes.
 * @event {{ value: string }} dds-complete - Fires when all slots are filled.
 * @event dds-focus - Fires when component gains focus.
 * @event dds-blur - Fires when component loses focus.
 *
 * @slot default - Slot for code-puncher-group, code-puncher-slot, and code-puncher-separator elements.
 *
 * @csspart base - The main container.
 * @csspart slots - The slots container.
 * @csspart label - The label element.
 * @csspart description - The description element.
 * @csspart feedback - The feedback element.
 */
@customElement('dap-ds-code-puncher')
export default class DapDSCodePuncher extends GenericFormElement {
  @property({ type: Boolean, reflect: true }) mask = false
  @property() pattern = '[0-9]'
  @property() placeholder = ''
  @property() autocomplete = 'one-time-code'

  @state() private _slots: DapDSCodePuncherSlot[] = []
  @state() private _activeIndex = -1
  @state() private _chars: string[] = []
  @state() private _hasFocus = false

  private _rawValue = ''

  static readonly styles = unsafeCSS(styles)

  private _patternRegex: RegExp = /^[0-9]$/
  private _provider!: ContextProvider<typeof codePuncherContext>

  private _getContextValue(): CodePuncherContext {
    return {
      registerSlot: this._registerSlot.bind(this),
      unregisterSlot: this._unregisterSlot.bind(this),
      getCharAt: this._getCharAt.bind(this),
      setCharAt: this._setCharAt.bind(this),
      focusSlot: this._focusSlot.bind(this),
      handleKeydown: this._handleKeydown.bind(this),
      handlePaste: this._handlePaste.bind(this),
      activeIndex: this._activeIndex,
      mask: this.mask,
      disabled: this.disabled,
      readonly: this.readonly,
      size: this.size as CommonFormElementSize,
      placeholder: this.placeholder,
      pattern: this._patternRegex,
      status: this.status,
      autocomplete: this.autocomplete,
      slotCount: this._slots.length,
    }
  }

  connectedCallback(): void {
    super.connectedCallback()
    // Initialize the context provider
    this._provider = new ContextProvider(this, {
      context: codePuncherContext,
      initialValue: this._getContextValue(),
    })
  }

  private _updateContext(): void {
    if (this._provider) {
      this._provider.setValue(this._getContextValue())
    }
  }

  get value(): string {
    return this._chars.join('')
  }

  set value(v: string) {
    this._rawValue = v
    if (this._slots.length === 0) return
    const chars = v.split('')
    this._chars = Array.from(
      { length: this._slots.length },
      (_, i) => chars[i] ?? '',
    )
    this.internals.setFormValue(this.value)
    this.requestUpdate()
  }

  protected willUpdate(changedProperties: PropertyValueMap<any>): void {
    super.willUpdate(changedProperties)
    if (changedProperties.has('pattern')) {
      this._patternRegex = new RegExp(`^${this.pattern}$`)
    }
    // Update context when relevant properties change
    this._updateContext()
  }

  private _registerSlot(slot: DapDSCodePuncherSlot): number {
    const index = this._slots.length
    this._slots = [...this._slots, slot]
    this._chars = [...this._chars, this._rawValue[index] ?? '']
    this.internals.setFormValue(this.value)
    this.requestUpdate()
    return index
  }

  private _unregisterSlot(slot: DapDSCodePuncherSlot): void {
    const index = this._slots.indexOf(slot)
    if (index > -1) {
      this._slots = this._slots.filter(s => s !== slot)
      this._chars = this._chars.filter((_, i) => i !== index)
      this.requestUpdate()
    }
  }

  private _getCharAt(index: number): string {
    return this._chars[index] || ''
  }

  private _setCharAt(index: number, char: string): void {
    const oldValue = this.value
    this._chars = this._chars.map((c, i) => (i === index ? char : c))
    this.internals.setFormValue(this.value)

    this.emit('dds-input', { value: this.value, index })

    if (this.value !== oldValue) {
      this.emit('dds-change', { value: this.value })
    }

    // Check if complete
    if (
      this._chars.length > 0 &&
      this._chars.every(c => c !== '') &&
      char !== ''
    ) {
      this.emit('dds-complete', { value: this.value })
    }

    this.requestUpdate()
    this.setValidity()
  }

  private _focusSlot(index: number): void {
    if (index >= 0 && index < this._slots.length) {
      this._activeIndex = index
      this._slots[index]?.focus()
      this.requestUpdate()
    }
  }

  private _handleKeydown(index: number, event: KeyboardEvent): void {
    const char = this._chars[index] || ''

    switch (event.key) {
      case 'Backspace':
        if (char === '' && index > 0) {
          // Empty slot, move to previous and clear
          this._setCharAt(index - 1, '')
          this._focusSlot(index - 1)
        } else {
          // Clear current
          this._setCharAt(index, '')
        }
        event.preventDefault()
        break

      case 'Delete':
        this._setCharAt(index, '')
        event.preventDefault()
        break

      case 'ArrowLeft':
        if (index > 0) {
          this._focusSlot(index - 1)
        }
        event.preventDefault()
        break

      case 'ArrowRight':
        if (index < this._slots.length - 1) {
          this._focusSlot(index + 1)
        }
        event.preventDefault()
        break

      case 'Home':
        this._focusSlot(0)
        event.preventDefault()
        break

      case 'End':
        this._focusSlot(this._slots.length - 1)
        event.preventDefault()
        break
    }
  }

  private _handlePaste(event: ClipboardEvent): void {
    event.preventDefault()

    const pastedText = event.clipboardData?.getData('text') || ''
    // Remove common separators and filter by pattern
    const cleanedText = pastedText
      .replace(/[-·\s]/g, '')
      .split('')
      .filter(char => this._patternRegex.test(char))

    // Distribute characters starting from first slot
    cleanedText.slice(0, this._slots.length).forEach((char, i) => {
      this._chars[i] = char
    })

    this.internals.setFormValue(this.value)
    this.emit('dds-change', { value: this.value })

    // Focus last filled slot or next empty
    const lastFilledIndex = Math.min(
      cleanedText.length - 1,
      this._slots.length - 1,
    )
    const nextEmptyIndex = this._chars.findIndex(c => c === '')

    if (nextEmptyIndex > -1) {
      this._focusSlot(nextEmptyIndex)
    } else {
      this._focusSlot(lastFilledIndex)
    }

    // Check if complete
    if (this._chars.every(c => c !== '')) {
      this.emit('dds-complete', { value: this.value })
    }

    this.requestUpdate()
  }

  private _handleFocusIn(): void {
    if (!this._hasFocus) {
      this._hasFocus = true
      this.emit('dds-focus')
    }
  }

  private _handleFocusOut(event: FocusEvent): void {
    // Check if focus is moving to another element within this component
    const relatedTarget = event.relatedTarget as HTMLElement
    if (!this.contains(relatedTarget)) {
      this._hasFocus = false
      this._activeIndex = -1
      this.emit('dds-blur')
      this.requestUpdate()
    }
  }

  setActiveIndex(index: number): void {
    this._activeIndex = index
    this.requestUpdate()
  }

  public get focusElement(): HTMLElement {
    return this._slots[0] || this
  }

  override focus(): void {
    // Focus first empty slot or first slot
    const firstEmptyIndex = this._chars.findIndex(c => c === '')
    const targetIndex = firstEmptyIndex > -1 ? firstEmptyIndex : 0
    this._focusSlot(targetIndex)
  }

  setValidity(): void {
    if (this.required && this._chars.some(c => c === '')) {
      this.internals.setValidity(
        { valueMissing: true },
        'Please fill in all digits',
        this._slots[0],
      )
    } else {
      this.internals.setValidity({})
    }
  }

  render() {
    return html`
      <div
        part="base"
        class=${clsx('code-puncher', `code-puncher--${this.size}`, {
          'code-puncher--disabled': this.disabled,
          'code-puncher--readonly': this.readonly,
          'code-puncher--success': this.status === 'success',
          'code-puncher--error': this.status === 'error',
          'code-puncher--focused': this._hasFocus,
        })}
        role="group"
        aria-label=${ifDefined(this.label || 'Code input')}
        @focusin=${this._handleFocusIn}
        @focusout=${this._handleFocusOut}>
        ${this.label || this.tooltip
          ? html`
              <div class="label-container">
                ${this.label
                  ? html`<dap-ds-form-label
                      part="label"
                      id="label"
                      .size=${this.size}
                      .required=${this.required}
                      .optional=${this.optional}
                      .disabled=${this.disabled}
                      class=${clsx('label')}
                      .noMargin=${!!this.description}
                      label=${ifDefined(this.label)}>
                    </dap-ds-form-label>`
                  : nothing}
                ${this.tooltip
                  ? html`<dap-ds-tooltip
                      part="tooltip"
                      content=${this.tooltip}
                      mode="toggle"
                      floatingStrategy="fixed"
                      placement=${this.tooltipPlacement}>
                      <dap-ds-icon-button
                        clean
                        class="info-button"
                        slot="trigger"
                        aria-label=${this.tooltipAriaLabel ||
                        'More information'}
                        size=${(this.size as 'xs' | 'sm' | 'lg') || 'sm'}
                        iconSize=${this.size === 'xs' ? '16' : '22'}>
                        <dap-ds-icon
                          name="information-line"
                          class="info-button"></dap-ds-icon>
                      </dap-ds-icon-button>
                    </dap-ds-tooltip>`
                  : nothing}
              </div>
            `
          : nothing}
        ${this.description
          ? html`
              <dap-ds-typography
                part="description"
                variant="description"
                class=${clsx('input-description')}
                size=${ifDefined(this.size as CommonFormElementSize)}
                id="description">
                ${this.description}
              </dap-ds-typography>
            `
          : nothing}
        <div part="slots" class="code-puncher__slots">
          <slot></slot>
        </div>
        ${this.feedback && this._renderFeedback()}
      </div>
    `
  }

  private _renderFeedback() {
    return html`
      <dap-ds-feedback
        part="feedback"
        role="alert"
        feedbackType=${ifDefined(this.feedbackType)}
        feedbackSize="${ifDefined(this.size as 'lg' | 'sm' | 'xs')}"
        .subtle=${this.subtle}
        id="feedback">
        <slot
          name="feedback-icon"
          @slotchange="${this._handleSlotChange}"></slot>
        ${this.feedback}
      </dap-ds-feedback>
    `
  }

  private _handleSlotChange(event: Event) {
    const target = event?.target as HTMLElement
    target.setAttribute('slot', 'icon')
  }
}
